import html2canvas from 'html2canvas-pro';
import type { ClickOffsetPct, ClickXY, ElementRect, SourceInfo, Viewport } from '@pinmerge/shared';
import { palette, colors, alpha } from '@pinmerge/tokens';

interface CaptureResult {
  rect: ElementRect;
  clickXY: ClickXY;
  clickOffsetPct: ClickOffsetPct;
  viewport: Viewport;
  pageUrl: string;
  pageTitle: string;
  screenshotBlob: Blob;
  screenshotDataUrl: string;
  /** Cropped element-area PNG. Null when the resolved element fell outside the
   *  visible viewport (rare — html2canvas would have produced an empty crop). */
  thumbnailBlob: Blob | null;
  elementHtml: string;
  elementSelector: string;
  /** Source-code anchor for the resolved element, derived from the optional
   *  HostAdapter's resolveSource(). All null when no adapter resolved a source
   *  (script-tag channel, or adapter returned null) — snake_case to map 1:1 to
   *  the API/DB columns the submit path serializes. */
  source_file: string | null;
  source_line: number | null;
  source_column: number | null;
  component_name: string | null;
}

// Thumbnail dimensions, in CSS pixels, before DPR scaling. The crop box around
// the element is padded by THUMB_PADDING on each side and then fitted into a
// max bounding box so very large elements (full-width hero) don't dominate.
const THUMB_PADDING = 16;
const THUMB_MAX_W = 480;
const THUMB_MAX_H = 320;

const HOST_ID = 'pinmerge-host';
const MARKER_ATTR = 'data-pinmerge-marker';
// Walk up parents while the clicked element is below this size — avoids "just a dot" cases
// when the user clicks a small icon inside a button.
const MIN_TARGET_PX = 24;

// Mounts the marker overlays for the duration of the html2canvas call so the screenshot
// shows the comment box + click dot baked in. Removes them in `finally` no matter what.
//
// `opts.resolveSource` lets the call site supply the host adapter's source
// resolver. When omitted (script-tag channel) or when it returns null, all
// source_* fields come back null → behaviour unchanged (I3 graceful
// degradation).
export async function capturePage(
  target: HTMLElement,
  click: ClickXY,
  opts?: { resolveSource?: (el: HTMLElement) => SourceInfo | null },
): Promise<CaptureResult> {
  const resolved = resolveMeaningfulTarget(target);
  // Resolve the source anchor for the *resolved* element (the same node the
  // selector/screenshot describe), not the raw click target.
  const src = opts?.resolveSource?.(resolved) ?? null;
  const rect = resolved.getBoundingClientRect();
  const elementRect: ElementRect = {
    x: Math.round(rect.left),
    y: Math.round(rect.top),
    w: Math.round(rect.width),
    h: Math.round(rect.height),
  };

  const viewport: Viewport = {
    w: window.innerWidth,
    h: window.innerHeight,
    dpr: window.devicePixelRatio || 1,
  };

  const scale = Math.min(viewport.dpr, 2);

  const markers = mountMarkers(rect, click);
  let canvas: HTMLCanvasElement;
  try {
    canvas = await html2canvas(document.body, {
      useCORS: true,
      logging: false,
      backgroundColor: palette.white,
      scale,
      ignoreElements: (el) =>
        el.id === HOST_ID ||
        el.hasAttribute('data-pinmerge-pin') ||
        el.hasAttribute('data-pinmerge-hover'),
    });
  } finally {
    markers.forEach((m) => m.remove());
  }

  const finalCanvas = cropToViewport(canvas, viewport, scale);

  const screenshotBlob = await new Promise<Blob>((resolve, reject) => {
    finalCanvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))), 'image/png');
  });
  const screenshotDataUrl = finalCanvas.toDataURL('image/png');

  const thumbnailBlob = await cropThumbnail(finalCanvas, elementRect, viewport, scale);

  return {
    rect: elementRect,
    clickXY: click,
    clickOffsetPct: clickOffsetPctOf(rect, click),
    viewport,
    pageUrl: window.location.href,
    pageTitle: document.title,
    screenshotBlob,
    screenshotDataUrl,
    thumbnailBlob,
    elementHtml: trimHtml(resolved.outerHTML),
    elementSelector: buildSelector(resolved),
    source_file: src?.file ?? null,
    source_line: src?.line ?? null,
    source_column: src?.column ?? null,
    component_name: src?.component ?? null,
  };
}

/** Click position as a fraction of the element rect. Clamped to [0, 1] so a
 *  borderline click doesn't end up outside the box on the rendering side. */
export function clickOffsetPctOf(rect: DOMRect | ElementRect, click: ClickXY): ClickOffsetPct {
  const w = 'width' in rect ? rect.width : rect.w;
  const h = 'height' in rect ? rect.height : rect.h;
  const left = 'left' in rect ? rect.left : rect.x;
  const top = 'top' in rect ? rect.top : rect.y;
  if (w <= 0 || h <= 0) return { x: 0.5, y: 0.5 };
  return {
    x: clamp01((click.x - left) / w),
    y: clamp01((click.y - top) / h),
  };
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0.5;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

// Crops a thumbnail of the clicked element from the viewport-sized canvas.
// Returns null when the element rect is fully off-screen (which would yield an
// empty crop). The crop is fitted into THUMB_MAX_W × THUMB_MAX_H while keeping
// aspect ratio — small elements stay close to original size, huge banners get
// scaled down so the thumbnail stays readable.
async function cropThumbnail(
  src: HTMLCanvasElement,
  rect: ElementRect,
  viewport: Viewport,
  scale: number,
): Promise<Blob | null> {
  // Source rect in canvas pixels (canvas is viewport-sized, scaled by `scale`).
  const sx = Math.max(0, (rect.x - THUMB_PADDING) * scale);
  const sy = Math.max(0, (rect.y - THUMB_PADDING) * scale);
  const sxEnd = Math.min(src.width, (rect.x + rect.w + THUMB_PADDING) * scale);
  const syEnd = Math.min(src.height, (rect.y + rect.h + THUMB_PADDING) * scale);
  const sw = sxEnd - sx;
  const sh = syEnd - sy;
  if (sw <= 0 || sh <= 0) return null;

  // Target size: fit into the max box, preserve aspect ratio.
  const aspect = sw / sh;
  let dw = sw;
  let dh = sh;
  const maxW = THUMB_MAX_W * scale;
  const maxH = THUMB_MAX_H * scale;
  if (dw > maxW) {
    dw = maxW;
    dh = dw / aspect;
  }
  if (dh > maxH) {
    dh = maxH;
    dw = dh * aspect;
  }

  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(dw));
  out.height = Math.max(1, Math.round(dh));
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, sx, sy, sw, sh, 0, 0, out.width, out.height);

  void viewport;
  return new Promise<Blob | null>((resolve) => {
    out.toBlob((b) => resolve(b), 'image/png');
  });
}

export function resolveMeaningfulTarget(el: HTMLElement): HTMLElement {
  let node: HTMLElement = el;
  for (let i = 0; i < 3; i++) {
    const r = node.getBoundingClientRect();
    if (r.width >= MIN_TARGET_PX && r.height >= MIN_TARGET_PX) return node;
    const parent = node.parentElement;
    if (!parent || parent === document.body) return node;
    node = parent;
  }
  return node;
}

export function mountMarkers(viewportRect: DOMRect, click: ClickXY): HTMLElement[] {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  const docLeft = viewportRect.left + scrollX;
  const docTop = viewportRect.top + scrollY;
  const docClickX = click.x + scrollX;
  const docClickY = click.y + scrollY;

  const box = document.createElement('div');
  box.setAttribute(MARKER_ATTR, 'box');
  box.style.cssText = [
    'position: absolute',
    `left: ${docLeft}px`,
    `top: ${docTop}px`,
    `width: ${viewportRect.width}px`,
    `height: ${viewportRect.height}px`,
    `background: ${alpha(colors.pin.own, 0.18)}`,
    `border: 3px solid ${colors.pin.own}`,
    'border-radius: 4px',
    'pointer-events: none',
    'box-sizing: border-box',
    'z-index: 2147483646',
    'margin: 0',
    'padding: 0',
  ].join(';');

  const dot = document.createElement('div');
  dot.setAttribute(MARKER_ATTR, 'dot');
  const dotSize = 18;
  dot.style.cssText = [
    'position: absolute',
    `left: ${docClickX - dotSize / 2}px`,
    `top: ${docClickY - dotSize / 2}px`,
    `width: ${dotSize}px`,
    `height: ${dotSize}px`,
    `background: ${colors.pin.own}`,
    `border: 3px solid ${palette.white}`,
    'border-radius: 50%',
    `box-shadow: 0 0 0 1px ${alpha(palette.black, 0.15)}`,
    'pointer-events: none',
    'box-sizing: border-box',
    'z-index: 2147483646',
    'margin: 0',
    'padding: 0',
  ].join(';');

  document.body.appendChild(box);
  document.body.appendChild(dot);
  return [box, dot];
}

function cropToViewport(src: HTMLCanvasElement, viewport: Viewport, scale: number): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = Math.round(viewport.w * scale);
  out.height = Math.round(viewport.h * scale);
  const ctx = out.getContext('2d');
  if (!ctx) return src;

  ctx.fillStyle = palette.white;
  ctx.fillRect(0, 0, out.width, out.height);

  const sx = Math.round(window.scrollX * scale);
  const sy = Math.round(window.scrollY * scale);
  const sw = Math.min(out.width, src.width - sx);
  const sh = Math.min(out.height, src.height - sy);
  if (sw <= 0 || sh <= 0) return src;

  ctx.drawImage(src, sx, sy, sw, sh, 0, 0, sw, sh);
  return out;
}

export function trimHtml(html: string, max = 4000): string {
  return html.length > max ? `${html.slice(0, max)}…` : html;
}

// Walks up from the target, building one selector segment per ancestor. After each
// segment is prepended, checks whether the partial path already uniquely identifies the
// target. If so, returns early. This handles the "three cards with the same
// data-component" case correctly — data-component on its own isn't enough, but combined
// with nth-of-type and an outer ancestor, it becomes unique.
export function buildSelector(el: HTMLElement): string {
  if (el.id) {
    const sel = `#${CSS.escape(el.id)}`;
    if (matchesUniquely(sel, el)) return sel;
  }

  const segments: string[] = [];
  let node: HTMLElement | null = el;
  for (let i = 0; i < 8 && node && node !== document.body; i++) {
    segments.unshift(segmentFor(node));
    const path = segments.join(' > ');
    if (matchesUniquely(path, el)) return path;
    node = node.parentElement;
  }
  return segments.join(' > ');
}

function segmentFor(node: HTMLElement): string {
  const tag = node.tagName.toLowerCase();
  let part = tag;

  const dataComponent = node.getAttribute('data-component');
  if (dataComponent) {
    part += `[data-component="${dataComponent.replace(/"/g, '\\"')}"]`;
  } else if (node.id) {
    part += `#${CSS.escape(node.id)}`;
  } else if (node.classList.length > 0) {
    const cls = Array.from(node.classList).slice(0, 2).map((c: string) => `.${CSS.escape(c)}`).join('');
    part += cls;
  }

  // Always disambiguate by sibling position when there are multiple of the same tag.
  // Crucial for the "many cards with the same data-component" case.
  const parent = node.parentElement;
  if (parent) {
    const siblings = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
    if (siblings.length > 1) {
      const idx = siblings.indexOf(node) + 1;
      part += `:nth-of-type(${idx})`;
    }
  }
  return part;
}

function matchesUniquely(selector: string, target: HTMLElement): boolean {
  try {
    const matches = document.querySelectorAll(selector);
    return matches.length === 1 && matches[0] === target;
  } catch {
    return false;
  }
}
